import { makeAutoObservable } from "mobx";

import { CONTENT_WIDTH, SIDEBAR_WIDTH } from "@/style/sizes";
import { setClientCookie } from "@/utils/cookies/client";
import { COOKIE_KEYS } from "@/utils/cookies/keys";
import { isInsideMobileWebView } from "@/utils/mobileBridgeAuth";
import { localStorageProperty, sessionStorageProperty } from "@/utils/storage";

import type { LazyStore } from "./types";
import type { ChatKind } from "@/api";
import type { SymbolPaletteItemKey } from "@/components/doc/right-sidebar/symbol-palette/types";
import type { RootStore } from "@/store";

/**
 * A target the mounted page offers to the skip links: its `<main>`, or a document's AI assistant
 * and team chat.
 */
export type SkipLink = "mainContent" | "documentChats";

export type SidebarState =
  | "full" // fully visible
  | "collapsed" // manually collapsed
  | "hidden" // temporary hidden by shortcut cmd+\
  | "auto-collapsed"; // collapsed automatically when the window is too narrow

export type SidebarStatePersisted = "full" | "collapsed";

export type LeftSidebarTab = "docs" | "chats";

// Snapshot derived from cookies and the user-agent header on the server, so
// SSR and the first client render agree on the layout.
export type ServerViewState = {
  leftSidebarState: SidebarStatePersisted;
  rightSidebarState: SidebarStatePersisted;
  floatingChatInputHidden: boolean;
  isMobileWebView: boolean;
};

// keep in sync with the w-[54px] collapsed classes in LeftSidebar/RightSidebar
const COLLAPSED_SIDEBAR_WIDTH = 54;

const IS_SYMBOL_PALETTE_VISIBLE_LS_KEY = "corca.isSymbolPaletteVisible";
const SYMBOL_PALETTE_ACTIVE_KEY = "corca.symbolPaletteActiveKey";
export const IS_HINTS_PANEL_VISIBLE_LS_KEY = "corca.isHintsPanelVisible";

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
export class ViewStore implements LazyStore {
  rootStore: RootStore;

  leftSidebarState: SidebarState = "full";

  rightSidebarState: SidebarState = "full";

  private leftSidebarStateBeforeHide: SidebarStatePersisted = "full";

  private rightSidebarStateBeforeHide: SidebarStatePersisted = "full";

  leftSidebarTab: LeftSidebarTab = "docs";

  isMobileDocsVisible = false;

  isMobileDownloadVisible = false;

  isMobileMenuVisible = false;

  isRequestPermissionPanelVisible = true;

  isFloatingChatInputHidden = false;

  isAnyEditorFocused = false;

  isSymbolPaletteVisible = sessionStorageProperty.get<boolean>(
    IS_SYMBOL_PALETTE_VISIBLE_LS_KEY,
    true,
  );

  symbolPaletteActiveKey?: SymbolPaletteItemKey =
    sessionStorageProperty.get<SymbolPaletteItemKey>(SYMBOL_PALETTE_ACTIVE_KEY);

  isHintsPanelVisible: boolean =
    localStorageProperty.get<boolean>(IS_HINTS_PANEL_VISIBLE_LS_KEY) ?? false;

  /** The kind of chat whose input takes focus once it renders; see `requestChatInputFocus` */
  private chatInputFocusRequest: ChatKind | null = null;

  private stopChatInputFocusRequestListeners: (() => void) | undefined;

  /** What the mounted page offers to the skip links; see `useSkipLinks` */
  skipLinks = new Set<SkipLink>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<this, "stopChatInputFocusRequestListeners">(
      this,
      { stopChatInputFocusRequestListeners: false },
      { autoBind: true },
    );
  }

  initFromServerState = (state: ServerViewState) => {
    this.leftSidebarState = state.leftSidebarState;
    this.rightSidebarState = state.rightSidebarState;
    this.isFloatingChatInputHidden = state.floatingChatInputHidden;
    this.isMobileWebView = state.isMobileWebView;

    // When running inside the React Native WebView, hide both sidebars so the
    // editor fills the full screen. The mobile app provides its own native UI.
    if (state.isMobileWebView) {
      this.leftSidebarState = "hidden";
      this.rightSidebarState = "hidden";
    }
  };

  private persistSidebarLayout(side: "left" | "right") {
    const state = side === "left" ? this.leftSidebarState : this.rightSidebarState;
    if (state === "hidden" || state === "auto-collapsed") return;

    setClientCookie(
      side === "left" ? COOKIE_KEYS.leftSidebarState : COOKIE_KEYS.rightSidebarState,
      state,
      {
        path: "/",
        sameSite: "Lax",
        maxAgeSeconds: ONE_YEAR_SECONDS,
      },
    );
  }

  toggleLeftSidebar = (forceExpand?: boolean) => {
    this.rootStore.search.close();

    if (forceExpand === true) {
      this.leftSidebarState = "full";
      this.leftSidebarStateBeforeHide = "full";
      this.persistSidebarLayout("left");
      return;
    }

    if (this.leftSidebarState === "hidden") {
      this.leftSidebarState = this.leftSidebarStateBeforeHide;
      return;
    }

    this.leftSidebarState = this.leftSidebarState === "full" ? "collapsed" : "full";
    this.leftSidebarStateBeforeHide = this.leftSidebarState;
    this.persistSidebarLayout("left");
  };

  setLeftSidebarTab = (tab: LeftSidebarTab) => {
    this.leftSidebarTab = tab;
  };

  toggleRightSidebar = () => {
    if (this.rightSidebarState === "hidden") {
      this.rightSidebarState = this.rightSidebarStateBeforeHide;
      return;
    }
    this.rightSidebarState = this.rightSidebarState === "full" ? "collapsed" : "full";
    this.rightSidebarStateBeforeHide = this.rightSidebarState;
    this.persistSidebarLayout("right");
  };

  toggleRequestPermissionPanel = () => {
    this.isRequestPermissionPanelVisible = !this.isRequestPermissionPanelVisible;
  };

  private hideSidebars() {
    if (this.leftSidebarState === "full" || this.leftSidebarState === "collapsed") {
      this.leftSidebarStateBeforeHide = this.leftSidebarState;
    }
    if (this.rightSidebarState === "full" || this.rightSidebarState === "collapsed") {
      this.rightSidebarStateBeforeHide = this.rightSidebarState;
    }
    this.leftSidebarState = "hidden";
    this.rightSidebarState = "hidden";
  }

  private restoreSidebarsState() {
    this.leftSidebarState = this.leftSidebarStateBeforeHide;
    this.rightSidebarState = this.rightSidebarStateBeforeHide;
    this.reapplyAutoCollapse();
  }

  private lastWindowWidth = 0;
  private prevLeftFits?: boolean;
  private prevRightFits?: boolean;

  private get chatWidth() {
    const { chats } = this.rootStore;
    return chats?.isOpenChat ? chats.chatWindowWidth : 0;
  }

  syncChatLayout = () => {
    this.applyAutoCollapse(this.lastWindowWidth);
  };

  private sidebarPrefWidth(side: "left" | "right") {
    const state = side === "left" ? this.leftSidebarState : this.rightSidebarState;
    let pref: SidebarStatePersisted;
    if (state === "full" || state === "collapsed") {
      pref = state;
    } else {
      pref = side === "left" ? this.leftSidebarStateBeforeHide : this.rightSidebarStateBeforeHide;
    }
    return pref === "collapsed" ? COLLAPSED_SIDEBAR_WIDTH : SIDEBAR_WIDTH;
  }

  // Collapses sidebars when they would squeeze the document below its content
  // width: first the right one, then the left. An open chat takes its share of
  // the window first. Acts only when a threshold is crossed, so a manual expand
  // on a narrow window survives until the next crossing. Hidden sidebars are
  // not touched.
  applyAutoCollapse = (windowWidth: number) => {
    if (!windowWidth) return;
    this.lastWindowWidth = windowWidth;

    const availableWidth = windowWidth - this.chatWidth;
    const leftPrefWidth = this.sidebarPrefWidth("left");
    const rightFits =
      availableWidth >= CONTENT_WIDTH + leftPrefWidth + this.sidebarPrefWidth("right");
    const leftFits = availableWidth >= CONTENT_WIDTH + leftPrefWidth + COLLAPSED_SIDEBAR_WIDTH;

    if (rightFits !== this.prevRightFits) {
      if (!rightFits && this.rightSidebarState === "full") {
        this.rightSidebarState = "auto-collapsed";
      } else if (rightFits && this.rightSidebarState === "auto-collapsed") {
        this.rightSidebarState = "full";
      }
    }
    if (leftFits !== this.prevLeftFits) {
      if (!leftFits && this.leftSidebarState === "full") {
        this.leftSidebarState = "auto-collapsed";
      } else if (leftFits && this.leftSidebarState === "auto-collapsed") {
        this.leftSidebarState = "full";
      }
    }

    this.prevRightFits = rightFits;
    this.prevLeftFits = leftFits;
  };

  private reapplyAutoCollapse() {
    this.prevLeftFits = undefined;
    this.prevRightFits = undefined;
    this.applyAutoCollapse(this.lastWindowWidth);
  }

  private handleWindowResize = () => {
    this.applyAutoCollapse(window.innerWidth);
  };

  toggleTempHideSidebars = () => {
    const anyShowing = this.leftSidebarState !== "hidden" || this.rightSidebarState !== "hidden";
    if (anyShowing) {
      this.hideSidebars();
    } else {
      this.restoreSidebarsState();
    }
  };

  get isLeftSidebarCollapsed() {
    return this.leftSidebarState === "collapsed" || this.leftSidebarState === "auto-collapsed";
  }

  get isRightSidebarCollapsed() {
    return this.rightSidebarState === "collapsed" || this.rightSidebarState === "auto-collapsed";
  }

  get areSidebarsHidden() {
    return this.leftSidebarState === "hidden" && this.rightSidebarState === "hidden";
  }

  /**
   * Asks the input of a `kind` chat to take focus once it renders: the floating input or a chat
   * panel. The user's next key or pointer press drops the request, so a late render can't pull focus
   * from wherever they went meanwhile.
   */
  requestChatInputFocus(kind: ChatKind) {
    this.clearChatInputFocusRequest();
    this.chatInputFocusRequest = kind;
    const clear = () => this.clearChatInputFocusRequest();
    document.addEventListener("keydown", clear, true);
    document.addEventListener("pointerdown", clear, true);
    this.stopChatInputFocusRequestListeners = () => {
      document.removeEventListener("keydown", clear, true);
      document.removeEventListener("pointerdown", clear, true);
    };
  }

  isChatInputFocusRequested(kind: ChatKind) {
    return this.chatInputFocusRequest === kind;
  }

  clearChatInputFocusRequest() {
    this.chatInputFocusRequest = null;
    this.stopChatInputFocusRequestListeners?.();
    this.stopChatInputFocusRequestListeners = undefined;
  }

  toggleFloatingChatInput = (hidden?: boolean) => {
    this.isFloatingChatInputHidden = hidden ?? !this.isFloatingChatInputHidden;
    setClientCookie(COOKIE_KEYS.floatingChatInputHidden, String(this.isFloatingChatInputHidden), {
      path: "/",
      sameSite: "Lax",
      maxAgeSeconds: ONE_YEAR_SECONDS,
    });
  };

  toggleSymbolPalette = () => {
    this.isSymbolPaletteVisible = !this.isSymbolPaletteVisible;
    sessionStorageProperty.set(IS_SYMBOL_PALETTE_VISIBLE_LS_KEY, this.isSymbolPaletteVisible);
  };

  addSkipLinks(links: SkipLink[]) {
    links.forEach((link) => this.skipLinks.add(link));
  }

  removeSkipLinks(links: SkipLink[]) {
    links.forEach((link) => this.skipLinks.delete(link));
  }

  toggleHintsPanel = () => {
    this.isHintsPanelVisible = !this.isHintsPanelVisible;
    localStorageProperty.set(IS_HINTS_PANEL_VISIBLE_LS_KEY, this.isHintsPanelVisible);
  };

  setActivePaletteSymbolKey = (key: SymbolPaletteItemKey) => {
    this.symbolPaletteActiveKey = key;
    sessionStorageProperty.set(SYMBOL_PALETTE_ACTIVE_KEY, key);
  };

  toggleActivePaletteSymbolKey = (key: SymbolPaletteItemKey) => {
    if (this.symbolPaletteActiveKey === key) {
      this.symbolPaletteActiveKey = undefined;
      sessionStorageProperty.remove(SYMBOL_PALETTE_ACTIVE_KEY);
    } else {
      this.symbolPaletteActiveKey = key;
      sessionStorageProperty.set(SYMBOL_PALETTE_ACTIVE_KEY, key);
    }
  };

  private handleFocus = () => {
    let el = document.activeElement;
    while (el instanceof HTMLElement) {
      if (el.classList.contains("ProseMirror")) {
        this.isAnyEditorFocused = true;
        return;
      }
      el = el.parentElement;
    }
    this.isAnyEditorFocused = false;
  };

  isMobileWebView = isInsideMobileWebView();

  /**
   * SSR can't know the window size, so sidebars render in the persisted
   * (full/collapsed) layout but stay invisible until this runs in an effect
   * after hydration, applies auto-collapse with the real width, and reveals
   * them. Reading the width during the initial render instead would make the
   * client tree diverge from the SSR HTML.
   */
  isSidebarLayoutReady = false;

  applyClientLayout = () => {
    this.applyAutoCollapse(window.innerWidth);
    this.isSidebarLayoutReady = true;
  };

  lazyEnable(): void {
    if (typeof document !== "undefined") {
      document.addEventListener("focusin", this.handleFocus);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.handleWindowResize);
    }
  }

  dispose(): void {
    if (typeof document !== "undefined") {
      document.removeEventListener("focusin", this.handleFocus);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.handleWindowResize);
    }
  }
}
